import { useState } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  Stack,
  Chip,
  Divider,
  Alert
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import BugReportIcon from '@mui/icons-material/BugReport'
import Context from '../models/Context'
import { DotloopProfile } from '../models/DotloopTypes'
import { FUBUser } from '../services/fubApi'

interface ApiDebugPanelProps {
  fubContext?: Context
  selectedAgent?: FUBUser | null
  selectedProfile?: DotloopProfile | null
  lastApiRequest?: any
  lastApiResponse?: any
  lastApiError?: string | null
}

export default function ApiDebugPanel({
  fubContext,
  selectedAgent,
  selectedProfile,
  lastApiRequest,
  lastApiResponse,
  lastApiError
}: ApiDebugPanelProps) {
  const [expanded, setExpanded] = useState(false)

  // Only show in development or if debug mode is enabled
  const showDebug = process.env.NODE_ENV === 'development' || 
                    process.env.REACT_APP_DEBUG_MODE === 'true'

  if (!showDebug) {
    return null
  }

  const handleTestFubConnection = async () => {
    console.log('Testing FUB connection...')
    alert('Check browser console for FUB connection test results')
  }

  const handleTestDotloopConnection = async () => {
    console.log('Testing DotLoop connection...')
    try {
      const response = await fetch('/api/dotloop/auth/status')
      const data = await response.json()
      console.log('DotLoop auth status:', data)
      alert(`DotLoop Status: ${data.authenticated ? 'Connected' : 'Not Connected'}\nCheck console for details.`)
    } catch (error) {
      console.error('DotLoop connection test failed:', error)
      alert('DotLoop connection test failed. Check console for details.')
    }
  }

  return (
    <Box sx={{ mb: 2, mx: 2 }}>
      <Accordion 
        expanded={expanded} 
        onChange={() => setExpanded(!expanded)}
        sx={{ 
          border: '2px dashed #ff9800',
          backgroundColor: '#fff3e0'
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <BugReportIcon color="warning" />
            <Typography variant="subtitle1" fontWeight="bold">
              🔧 API Debug Panel
            </Typography>
            <Chip 
              label="DEV MODE" 
              size="small" 
              color="warning" 
              variant="outlined"
            />
          </Stack>
        </AccordionSummary>
        
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Connection Tests */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Connection Tests
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={handleTestFubConnection}
                >
                  Test FUB Connection
                </Button>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={handleTestDotloopConnection}
                >
                  Test DotLoop Connection
                </Button>
              </Stack>
            </Box>

            <Divider />

            {/* FUB Context */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                FollowUpBoss Context
              </Typography>
              {fubContext ? (
                <Box 
                  component="pre" 
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 200
                  }}
                >
                  {JSON.stringify(fubContext, null, 2)}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No FUB context available
                </Typography>
              )}
            </Box>

            {/* Selected Agent */}
            {selectedAgent && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Selected Agent
                </Typography>
                <Box 
                  component="pre" 
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto'
                  }}
                >
                  {JSON.stringify(selectedAgent, null, 2)}
                </Box>
              </Box>
            )}

            {/* Selected Profile */}
            {selectedProfile && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Selected DotLoop Profile
                </Typography>
                <Box 
                  component="pre" 
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto'
                  }}
                >
                  {JSON.stringify(selectedProfile, null, 2)}
                </Box>
              </Box>
            )}

            {/* Last API Request */}
            {lastApiRequest && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Last API Request
                </Typography>
                <Box 
                  component="pre" 
                  sx={{ 
                    backgroundColor: '#e3f2fd',
                    p: 1,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 200
                  }}
                >
                  {JSON.stringify(lastApiRequest, null, 2)}
                </Box>
              </Box>
            )}

            {/* Last API Response */}
            {lastApiResponse && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Last API Response
                </Typography>
                <Box 
                  component="pre" 
                  sx={{ 
                    backgroundColor: '#e8f5e9',
                    p: 1,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 200
                  }}
                >
                  {JSON.stringify(lastApiResponse, null, 2)}
                </Box>
              </Box>
            )}

            {/* Last API Error */}
            {lastApiError && (
              <Box>
                <Alert severity="error" sx={{ fontSize: '0.75rem' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Last API Error
                  </Typography>
                  {lastApiError}
                </Alert>
              </Box>
            )}

            {/* Environment Info */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Environment
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip 
                  label={`Node ENV: ${process.env.NODE_ENV}`} 
                  size="small" 
                />
                <Chip 
                  label={`Debug Mode: ${process.env.REACT_APP_DEBUG_MODE || 'false'}`} 
                  size="small" 
                />
                <Chip 
                  label={`FUB API: ${process.env.REACT_APP_FUB_API_KEY ? 'Configured' : 'Missing'}`} 
                  size="small"
                  color={process.env.REACT_APP_FUB_API_KEY ? 'success' : 'error'}
                />
                <Chip 
                  label={`DotLoop Client: ${process.env.REACT_APP_DOTLOOP_CLIENT_ID ? 'Configured' : 'Missing'}`} 
                  size="small"
                  color={process.env.REACT_APP_DOTLOOP_CLIENT_ID ? 'success' : 'error'}
                />
              </Stack>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

